import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useKb } from "@/features/knowledge-bases/hooks/useKbs";
import { DocumentTab } from "@/features/knowledge-bases/components/DocumentTab";
import { PermissionsTab } from "@/features/knowledge-bases/components/PermissionsTab";
import { SearchTab } from "@/features/knowledge-bases/components/SearchTab";
import { SettingsTab } from "@/features/knowledge-bases/components/SettingsTab";

type TabValue = "documents" | "permissions" | "search" | "settings";
const VALID_TABS: TabValue[] = [
  "documents",
  "permissions",
  "search",
  "settings",
];

export function KbDetailPage() {
  const { t } = useTranslation("knowledgeBases");
  const { kbId = "" } = useParams<{ kbId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabValue | null;
  const activeTab: TabValue = VALID_TABS.includes(tabParam as TabValue)
    ? (tabParam as TabValue)
    : "documents";

  const { data: kb, isLoading, isError, error, refetch } = useKb(kbId);

  if (isLoading) return <LoadingState />;
  if (isError || !kb)
    return <ErrorState error={error} onRetry={() => void refetch()} />;

  function setTab(next: string) {
    setSearchParams((sp) => {
      const params = new URLSearchParams(sp);
      params.set("tab", next);
      return params;
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t("eyebrow")}
        title={kb.name}
        description={kb.short_description ?? undefined}
        backTo="/knowledge-bases"
      />
      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="documents">{t("tabs.documents")}</TabsTrigger>
          <TabsTrigger value="permissions">{t("tabs.permissions")}</TabsTrigger>
          <TabsTrigger value="search">{t("tabs.search")}</TabsTrigger>
          <TabsTrigger value="settings">{t("tabs.settings")}</TabsTrigger>
        </TabsList>
        <TabsContent value="documents">
          <DocumentTab kb={kb} />
        </TabsContent>
        <TabsContent value="permissions">
          <PermissionsTab kb={kb} />
        </TabsContent>
        <TabsContent value="search">
          <SearchTab kb={kb} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab kb={kb} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
